import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import SubsWall, {
  STYLE_LABEL,
  STYLE_PALETTES,
  TIERS,
  findNonOverlappingPosition,
  tierFor,
  tierGift,
  tierLabel,
  unlocksFor,
  type SubTile,
  type Theme,
  type TileStyle,
} from "../components/SubsWall";
import "./SignWallFlow.css";

// ----------------------------------------------------------------------------
// Mock data — replaced by real /api/creator/:handle fetch in a later pass.
// ----------------------------------------------------------------------------

interface CreatorView {
  handle: string;
  display_name: string;
  avatar_url: string | null;
  theme: Theme;
  supporter_count: number;
  // Tiles already on the wall — the preview shows them so the supporter sees
  // where their new tile will land.
  existing_tiles: SubTile[];
}

function mockCreator(handle: string): CreatorView {
  const display = handle.charAt(0).toUpperCase() + handle.slice(1);
  // Sparse on purpose — the compose preview needs breathing room for the
  // new tile to land in without collision retries. The real wall on
  // /:handle uses the full set.
  const seed: Array<Partial<SubTile> & { name: string; message: string; monthlyCents: number; style: TileStyle }> = [
    { name: "Sarah K.",     message: "Your masterclass changed my career.",       monthlyCents: 500,   style: "spray" },
    { name: "Tom Park",     message: "Why I finally bought the camera.",          monthlyCents: 1000,  style: "neon", blink: true },
    { name: "Maya Chen",    message: "The new long-form journalism.",             monthlyCents: 5000,  style: "carved", badge: "shield", anniversaryMonths: 26 },
    { name: "Helena Voss",  message: "Patron since '23. Quality compounds.",      monthlyCents: 10000, style: "neon", blink: true, badge: "crown", anniversaryMonths: 30 },
  ];
  const tiles: SubTile[] = seed.map((s, i) => ({
    id: `existing-${i}`,
    name: s.name,
    message: s.message,
    style: s.style,
    color: STYLE_PALETTES[s.style][(i + 2) % 8],
    // Push existing tiles to the four corners so the supporter's new
    // tile lands cleanly in the center.
    xPct: [22, 78, 22, 78][i] ?? 50,
    yPct: [22, 22, 78, 78][i] ?? 50,
    monthlyCents: s.monthlyCents,
    anniversaryMonths: s.anniversaryMonths ?? 0,
    boostUntil: null,
    fadeStartedAt: null,
    badge: null,
    founderNumber: null,
  }));
  return {
    handle,
    display_name: display,
    avatar_url: null,
    theme: "neon",
    supporter_count: tiles.length,
    existing_tiles: tiles,
  };
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

const NEW_TILE_ID = "you";

export default function SignWallFlow(): JSX.Element {
  const { handle = "demo" } = useParams<{ handle: string }>();
  const creator = useMemo(() => mockCreator(handle), [handle]);
  const theme = creator.theme;

  // Form state.
  const [name, setName] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [monthlyCents, setMonthlyCents] = useState<number>(500);
  const [style, setStyle] = useState<TileStyle>("spray");
  const [color, setColor] = useState<string>(STYLE_PALETTES.spray[0]);
  const [emoji, setEmoji] = useState<string | null>(null);
  const [xPct, setXPct] = useState<number>(50);
  const [yPct, setYPct] = useState<number>(50);
  const [userMoved, setUserMoved] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  // Auto-place the editable tile near the canvas center, in the nearest
  // non-overlapping slot. Re-runs whenever the supporter changes their
  // monthly amount or style (which affects tile size). Once the user
  // manually moves the tile, we stop auto-placing.
  useEffect(() => {
    if (userMoved) return;
    const sized = { monthlyCents, style } as const;
    const slot = findNonOverlappingPosition(50, 50, sized, creator.existing_tiles, 1, creator.theme);
    setXPct(slot.x);
    setYPct(slot.y);
  }, [monthlyCents, style, creator.existing_tiles, creator.theme, userMoved]);

  const unlocks = unlocksFor(monthlyCents, theme);
  const tier = tierFor(monthlyCents);
  const gift = tierGift(monthlyCents);

  // Coerce style to whatever's unlocked at the current tier so the preview
  // can never show a locked style.
  const effectiveStyle: TileStyle = unlocks.styles.includes(style) ? style : unlocks.styles[unlocks.styles.length - 1];
  const palette = STYLE_PALETTES[effectiveStyle];
  const effectiveColor = palette.includes(color) ? color : palette[0];

  // The user's draft tile gets placed on top of the existing wall.
  const previewTiles: SubTile[] = useMemo(() => [
    ...creator.existing_tiles,
    {
      id: NEW_TILE_ID,
      name: name || "Your name",
      message: message || "Your message will live here.",
      style: effectiveStyle,
      color: effectiveColor,
      xPct,
      yPct,
      monthlyCents,
      anniversaryMonths: 0,
      boostUntil: null,
      fadeStartedAt: null,
      badge: null,
      emoji,
      founderNumber: null,
    },
  ], [creator.existing_tiles, name, message, effectiveStyle, effectiveColor, xPct, yPct, monthlyCents, emoji]);

  function handleMove(id: string, x: number, y: number): void {
    if (id !== NEW_TILE_ID) return;
    setXPct(x);
    setYPct(y);
    setUserMoved(true); // stop auto-placement once the user picks a spot
  }

  // Auto-Drop: ask the collision-aware placer for a slot starting at the
  // canvas center. Falls back to the farthest-from-everything sampler if
  // every center-spiral candidate overlaps (very full wall edge case).
  function autoDrop(): void {
    const sized = { monthlyCents, style: effectiveStyle } as const;
    const slot = findNonOverlappingPosition(50, 50, sized, creator.existing_tiles, 1, creator.theme);
    setXPct(slot.x);
    setYPct(slot.y);
    setUserMoved(true);
  }

  async function handleSubmit(): Promise<void> {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch("/api/subscriptions/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          creator_handle: handle,
          name: name.trim(),
          message: message.trim(),
          style: effectiveStyle,
          color: effectiveColor,
          x_pct: xPct,
          y_pct: yPct,
          monthly_cents: monthlyCents,
          emoji: emoji ?? null,
        }),
      });
      const json = (await res.json()) as { ok?: boolean; mode?: string; redirect_url?: string; error?: string };
      if (!res.ok || !json.ok) {
        // Surface server error to the user without crashing the page.
        alert(json.error || "Couldn't start checkout. Try again in a moment.");
        setSubmitting(false);
        return;
      }
      if (json.mode === "stripe" && json.redirect_url) {
        window.location.href = json.redirect_url;
        return;
      }
      // Fake mode: skip Stripe, jump straight to the signed state.
      setSubmitting(false);
      setSubmitted(true);
    } catch (err) {
      alert("Network error. Try again.");
      setSubmitting(false);
    }
  }

  if (submitted) {
    return <SignedState creator={creator} amount={monthlyCents / 100} gift={gift} />;
  }

  const valid = name.trim().length > 0;

  return (
    <div className={`sw-flow sw-flow-${theme}`}>
      <header className="sw-flow-head">
        <div className="sw-flow-creator">
          <div className="sw-flow-av">{creator.display_name[0]}</div>
          <div>
            <h1>Sign {creator.display_name}'s wall</h1>
            <div className="sw-flow-sub">
              {creator.supporter_count} supporters · stay named on the wall as long as you support
            </div>
          </div>
        </div>
      </header>

      <div className="sw-flow-body">
        <section className="sw-flow-wall">
          <SubsWall
            tiles={previewTiles}
            editableTileId={NEW_TILE_ID}
            onTileMove={handleMove}
            theme={theme}
            autoGrow
          />
          <div className="sw-flow-wall-hint">
            <span>Drag your tile (with the ⤧ handle) — or</span>
            <button type="button" className="sw-flow-autodrop" onClick={autoDrop}>
              Find me a spot
            </button>
          </div>
        </section>

        <aside className="sw-flow-form">
          <Field label="YOUR NAME">
            <input
              type="text" value={name} maxLength={24}
              onChange={(e) => setName(e.target.value)}
              placeholder="How you'll appear on the wall"
            />
          </Field>

          <Field label={`MESSAGE (${message.length}/160)`}>
            <input
              type="text" value={message} maxLength={160}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="A line of your own — they'll see it forever."
            />
          </Field>

          <Field label="MONTHLY SUPPORT">
            <input
              type="range" min={500} max={25000} step={100}
              value={monthlyCents}
              onChange={(e) => setMonthlyCents(Number(e.target.value))}
            />
            <div className="sw-flow-gift">
              <span className="sw-flow-gift-icon">{gift.icon}</span>
              <span>
                Buying them <strong>{gift.label}</strong> every month
                {" · "}
                <strong>${(monthlyCents / 100).toFixed(0)}/mo</strong>
              </span>
            </div>
            <div className="sw-flow-tier">
              Tier: <strong>{tierLabel(tier, theme)}</strong>
            </div>
          </Field>

          <Field label="STYLE">
            <div className="sw-flow-chips">
              {(["spray", "chalk", "marker", "neon", "sticker", "carved"] as TileStyle[]).map((s) => {
                const locked = !unlocks.styles.includes(s);
                const minCents = TIERS.find((t) => unlocksFor(t.min, theme).styles.includes(s))?.min ?? 0;
                return (
                  <button
                    key={s}
                    type="button"
                    disabled={locked}
                    onClick={() => { setStyle(s); setColor(STYLE_PALETTES[s][0]); }}
                    className={[
                      "sw-flow-chip",
                      effectiveStyle === s ? "sw-flow-chip-active" : "",
                      locked ? "sw-flow-chip-locked" : "",
                    ].filter(Boolean).join(" ")}
                    title={locked ? `Unlocks at $${minCents / 100}/mo` : ""}
                  >
                    {STYLE_LABEL[s]}{locked ? ` · $${minCents / 100}+` : ""}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="COLOR">
            <div className="sw-flow-colors">
              {palette.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  style={{ background: c }}
                  className={`sw-flow-swatch ${effectiveColor === c ? "sw-flow-swatch-active" : ""}`}
                  aria-label={c}
                />
              ))}
            </div>
          </Field>

          <Field label="EMOJI (optional)">
            <div className="sw-flow-emojis">
              <button
                type="button"
                onClick={() => setEmoji(null)}
                className={`sw-flow-emoji ${emoji === null ? "sw-flow-emoji-active" : ""}`}
                aria-label="no emoji"
              >
                <span className="sw-flow-emoji-none">∅</span>
              </button>
              {["🎬","🎙️","🎨","🎮","🎵","✍️","📚","🧠","🚀","⚡","🔥","💎","🌟","❤️","☕","🍕","🍣","🍷","✈️","🐺","🦊","🌌","🪐","🎸","📷"].map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setEmoji(e)}
                  className={`sw-flow-emoji ${emoji === e ? "sw-flow-emoji-active" : ""}`}
                  aria-label={`emoji ${e}`}
                >
                  {e}
                </button>
              ))}
            </div>
          </Field>
        </aside>
      </div>

      <div className="sw-flow-cta-bar">
        <div className="sw-flow-cta-summary">
          {gift.icon} <strong>${(monthlyCents / 100).toFixed(0)}/mo</strong> · {tierLabel(tier, theme)} tier · 4.9% fee
        </div>
        <button
          type="button"
          className="sw-flow-cta"
          disabled={!valid || submitting}
          onClick={handleSubmit}
        >
          {submitting ? "Signing…" : `Sign the wall · $${(monthlyCents / 100).toFixed(0)}/mo`}
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Bits
// ----------------------------------------------------------------------------

function Field(props: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="sw-flow-field">
      <div className="sw-flow-field-label">{props.label}</div>
      {props.children}
    </label>
  );
}

function SignedState(props: {
  creator: CreatorView;
  amount: number;
  gift: { icon: string; label: string };
}): JSX.Element {
  return (
    <div className="sw-flow sw-flow-atelier sw-flow-done">
      <div className="sw-flow-done-card">
        <div className="sw-flow-done-emoji">{props.gift.icon}</div>
        <h1>You're on the wall.</h1>
        <p>
          Your tile is now part of <strong>{props.creator.display_name}'s</strong> wall — for as long as
          you support at <strong>${props.amount}/mo</strong>. Cancel anytime; you'll fade over 7 days.
        </p>
        <p className="sw-flow-done-meta">
          Buying them {props.gift.label} every month · billed monthly
        </p>
        <a className="sw-flow-cta sw-flow-cta-ghost" href={`/${props.creator.handle}`}>
          See your tile on the wall →
        </a>
      </div>
    </div>
  );
}
